import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Grid,
  TextField,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useUser } from "../UserContext";
import axios from "axios";
import SearchBarAccount from "../Components/SearchBarAccount";
import OptionalFieldsModal from "./OptionalFieldsModal";

interface Product {
  value: string;
  label: string;
}

interface account {
  id: number;
  account: string;
  address: string;
  city: string;
  state_code: string;
}

interface BookingRow {
  id: string;
  accountId: number | null;
  accountName: string;
  eventName: string;
  eventDesc: string;
  products: Product[];
  poolingMethod: string;
  slotType: string;
}

const MassBookings: React.FC = () => {
  const { currentUser } = useUser();
  const [products, setProducts] = useState<Product[]>([]);
  const [_accounts, setaccounts] = useState<account[]>([]);
  const [processing, setProcessing] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<{
    isValid: boolean;
    agencyName?: string;
    discount?: number;
  } | null>(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentRowId, setCurrentRowId] = useState<string | null>(null);

  // Initialize bookingRows with a default row
  const [bookingRows, setBookingRows] = useState<BookingRow[]>([
    {
      id: Math.random().toString(36).substr(2, 9),
      accountId: null,
      accountName: "",
      eventName: "",
      eventDesc: "Booked by Promo Raven",
      products: [],
      poolingMethod: "Standard",
      slotType: "Standard",
    },
  ]);

  useEffect(() => {
    fetchProducts();
    fetchaccounts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/brands/products`
      );
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchaccounts = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/accounts/accounts`,
        {
          params: { userId: currentUser?.user_id },
        }
      );
      setaccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  };

  const createNewRow = (copyFrom?: BookingRow): BookingRow => ({
    id: Math.random().toString(36).substr(2, 9),
    accountId: null,
    accountName: "",
    eventName: copyFrom?.eventName || "",
    eventDesc: copyFrom?.eventDesc || "Booked by Promo Raven",
    products: copyFrom?.products || [],
    poolingMethod: copyFrom?.poolingMethod || "Standard",
    slotType: copyFrom?.slotType || "Standard",
  });

  const addNewRow = (copyFrom?: BookingRow) => {
    setBookingRows([...bookingRows, createNewRow(copyFrom)]);
  };

  const duplicateRow = (index: number) => {
    const rowToCopy = bookingRows[index];
    addNewRow(rowToCopy);
  };

  const removeRow = (id: string) => {
    setBookingRows(bookingRows.filter((row) => row.id !== id));
  };

  const updateRow = (id: string, field: keyof BookingRow, value: any) => {
    setBookingRows(
      bookingRows.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  const handleExecuteMassBooking = async () => {
    setProcessing(true);
    try {
      // This will be replaced with the algorithmic booking logic
      console.log("Executing mass booking with rows:", bookingRows);
      // Clear all rows after successful creation
      setBookingRows([]);
    } catch (error) {
      console.error("Error creating events:", error);
    } finally {
      setProcessing(false);
    }
  };

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) return;

    setApplyingPromo(true);
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/ambassadors/verify-promo-code`,
        {
          code: promoCode,
        }
      );
      setAppliedPromo(response.data);
    } catch (error) {
      console.error("Error verifying promo code:", error);
      setAppliedPromo(null);
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleOpenModal = (rowId: string) => {
    setCurrentRowId(rowId);
    setModalOpen(true);
  };

  const handleDescriptionChange = (description: string) => {
    if (currentRowId) {
      updateRow(currentRowId, "eventDesc", description);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Mass Booking Tool
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Create multiple events across different locations
      </Typography>

      {/* Booking rows */}
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        {bookingRows.map((row, index) => (
          <Grid
            container
            spacing={2}
            key={row.id}
            sx={{
              mb: 2,
              alignItems: "center",
              backgroundColor: "background.paper",
              p: 1,
              borderRadius: 1,
            }}
          >
            <Grid item xs={2.75}>
              <SearchBarAccount boxSize="small" onSelectaccount={() => {}} />
            </Grid>
            <Grid item xs={2.75}>
              <Autocomplete
                multiple
                options={products}
                value={row.products}
                onChange={(_, newValue) =>
                  updateRow(row.id, "products", newValue)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Products"
                    size="small"
                    sx={{
                      "& .MuiAutocomplete-tag": {
                        maxWidth: "calc(100% - 32px)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "inline-block",
                      },
                      "& .MuiAutocomplete-inputRoot": {
                        flexWrap: "nowrap",
                      },
                    }}
                  />
                )}
                size="small"
              />
            </Grid>
            <Grid item xs={2.5}>
              <TextField
                fullWidth
                label="Event Name"
                value={row.eventName}
                onChange={(e) => updateRow(row.id, "eventName", e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Pool Type</InputLabel>
                <Select
                  value={row.poolingMethod}
                  onChange={(e) =>
                    updateRow(row.id, "poolingMethod", e.target.value)
                  }
                  label="Pool Type"
                >
                  <MenuItem value="Standard">Standard</MenuItem>
                  <MenuItem value="Premium">
                    Premium{" "}
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      (+$10)
                    </Typography>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Slot Type</InputLabel>
                <Select
                  value={row.slotType}
                  onChange={(e) =>
                    updateRow(row.id, "slotType", e.target.value)
                  }
                  label="Slot Type"
                >
                  <MenuItem value="Standard">Standard</MenuItem>
                  <MenuItem value="Premium">
                    Premium{" "}
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      (+$10)
                    </Typography>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={1}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <IconButton
                  onClick={() => handleOpenModal(row.id)}
                  color="primary"
                  size="small"
                >
                  <MoreVertIcon />
                </IconButton>
                <IconButton
                  onClick={() => duplicateRow(index)}
                  color="primary"
                  size="small"
                >
                  <ContentCopyIcon />
                </IconButton>
                <IconButton
                  onClick={() => removeRow(row.id)}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        ))}

        {/* Add new row button */}
        <Button
          startIcon={<AddIcon />}
          onClick={() => addNewRow()}
          variant="outlined"
          sx={{ mt: 2 }}
        >
          Add New Event
        </Button>
      </Paper>

      {/* Execute button and Promo Code section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            size="small"
            label="Agency Promo Code"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            disabled={appliedPromo !== null}
            sx={{ width: 150 }}
          />
          <Button
            variant="outlined"
            onClick={handleApplyPromoCode}
            disabled={
              !promoCode.trim() || applyingPromo || appliedPromo !== null
            }
          >
            {applyingPromo ? "Applying..." : "Apply Deal"}
          </Button>
          {appliedPromo && (
            <Typography
              color="success.main"
              sx={{
                display: "flex",
                alignItems: "center",
                ml: 2,
                fontSize: "0.875rem",
              }}
            >
              {`${appliedPromo.agencyName} code applied`}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          color="primary"
          disabled={bookingRows.length === 0 || processing}
          onClick={handleExecuteMassBooking}
          size="large"
        >
          {processing ? "Processing..." : "Launch"}
        </Button>
      </Box>

      {/* Optional Fields Modal */}
      <OptionalFieldsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        description={
          currentRowId
            ? bookingRows.find((row) => row.id === currentRowId)?.eventDesc ||
              ""
            : ""
        }
        onDescriptionChange={handleDescriptionChange}
      />
    </Paper>
  );
};

export default MassBookings;
